<template>
  <div class="p-4 md:p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-5">
      <div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Utenti bloccati</h2>
        <p class="text-sm text-gray-500 mt-0.5">
          Account in blacklist · {{ sortByUnpaid ? 'ordinati per importo non pagato ↓' : 'ordine di inserimento decrescente' }}
        </p>
      </div>
      <button
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-white text-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 text-sm transition"
        :disabled="loading"
        @click="refresh"
      >
        <span class="material-icons text-sm" :class="{ 'animate-spin': loading }">refresh</span>
        Aggiorna
      </button>
    </div>

    <!-- Filtri -->
    <div class="flex flex-wrap gap-3 mb-4">
      <div class="relative flex-1 min-w-[200px]">
        <span class="material-icons absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">search</span>
        <input
          v-model="filters.q"
          type="text"
          placeholder="Cerca per nome / email..."
          class="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          @input="onSearch"
        />
      </div>
      <select
        v-model="filters.profile_type"
        class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        @change="refresh"
      >
        <option value="">Tutti i profili</option>
        <option value="FOREIGN">🌍 Estero (FOREIGN)</option>
        <option value="PRIVATE">🧍 Privato (PRIVATE)</option>
        <option value="BUSINESS">🏢 Azienda (BUSINESS)</option>
      </select>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="space-y-2">
      <div
        v-for="i in 8"
        :key="i"
        class="h-14 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"
      ></div>
    </div>

    <!-- Empty -->
    <div
      v-else-if="!blockedUsers.length"
      class="bg-white dark:bg-gray-900 rounded-xl shadow p-12 text-center text-gray-400"
    >
      <span class="material-icons text-5xl mb-3 block">block</span>
      <p class="text-lg font-medium">Nessun utente bloccato.</p>
      <p v-if="filters.q || filters.profile_type" class="text-sm mt-1">
        Prova a rimuovere i filtri.
      </p>
    </div>

    <!-- Tabella -->
    <div v-else class="bg-white dark:bg-gray-900 rounded-xl shadow overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-500 uppercase">
            <th class="px-4 py-2 text-left">Account</th>
            <th class="px-4 py-2 text-left hidden md:table-cell">Email</th>
            <th class="px-4 py-2 text-left hidden lg:table-cell">Profilo</th>
            <th
              class="px-4 py-2 text-right hidden sm:table-cell cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
              @click="sortByUnpaid = !sortByUnpaid"
            >
              Non pagato
              <span class="material-icons text-xs align-middle ml-0.5">{{ sortByUnpaid ? 'arrow_downward' : 'unfold_more' }}</span>
            </th>
            <th class="px-4 py-2 text-left">Motivo blocco</th>
            <th class="px-4 py-2 text-right">Azioni</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
          <tr
            v-for="user in blockedUsers"
            :key="user.uid"
            class="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition"
          >
            <!-- Account -->
            <td class="px-4 py-3">
              <p class="font-medium text-gray-900 dark:text-white">
                {{ user.name }} {{ user.surname }}
                <span class="text-xs text-gray-400 ml-1">#{{ user.uid }}</span>
              </p>
              <p class="text-xs text-gray-400 md:hidden truncate max-w-[160px]">{{ user.email }}</p>
            </td>

            <!-- Email -->
            <td class="px-4 py-3 text-gray-500 hidden md:table-cell">{{ user.email }}</td>

            <!-- Profilo -->
            <td class="px-4 py-3 hidden lg:table-cell">
              <span
                v-if="user.profile_type"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                :class="profileBadge(user.profile_type)"
              >
                {{ profileEmoji(user.profile_type) }} {{ user.profile_type }}
              </span>
              <span v-else class="text-gray-300 text-xs">—</span>
            </td>

            <!-- Non pagato -->
            <td class="px-4 py-3 text-right hidden sm:table-cell">
              <span
                v-if="user.unpaid_eur > 0"
                class="text-red-600 dark:text-red-400 font-semibold"
              >{{ formatEur(user.unpaid_eur) }}</span>
              <span v-else class="text-gray-300">—</span>
            </td>

            <!-- Motivo -->
            <td class="px-4 py-3 text-gray-500 max-w-[200px]">
              <p
                class="truncate text-xs"
                :title="translateReason(user.reason)"
              >
                {{ translateReason(user.reason) || '—' }}
              </p>
            </td>

            <!-- Azioni -->
            <td class="px-4 py-3 text-right">
              <div class="flex items-center justify-end gap-2">
                <router-link
                  :to="{ name: 'menu.antifrode.utenti.detail', params: { uid: user.uid } }"
                  class="text-blue-600 hover:underline text-xs"
                >
                  Dettaglio
                </router-link>
                <button
                  class="text-xs px-2 py-1 rounded-lg border border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/20 transition"
                  @click="openUnblockModal(user)"
                >
                  Sblocca
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Totale -->
      <div class="px-4 py-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400">
        {{ blockedUsers.length }} utent{{ blockedUsers.length === 1 ? 'e' : 'i' }} bloccati
        <span v-if="blockedUsers.length >= 100"> · mostra solo i primi 100, usa la ricerca per trovarne altri</span>
      </div>
    </div>

    <!-- Modal sblocco -->
    <flow-modal v-if="unblockModal.open" @backdrop="closeUnblockModal">
      <template #header>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Sblocca utente
        </h3>
      </template>
      <template #body>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Stai sbloccando
          <strong class="text-gray-900 dark:text-white">
            {{ unblockModal.user?.name }} {{ unblockModal.user?.surname }}
          </strong>
          ({{ unblockModal.user?.email }}).
        </p>
        <p v-if="unblockModal.user?.reason" class="text-xs text-gray-400 bg-gray-50 dark:bg-gray-800 rounded p-2 mb-4">
          Motivo blocco originale: "{{ unblockModal.user.reason }}"
        </p>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Motivazione sblocco <span class="text-red-500">*</span>
        </label>
        <textarea
          v-model="unblockModal.reason"
          class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-green-500 focus:outline-none"
          rows="3"
          placeholder="Es: Errore di classificazione – verificato da Mario il 29/05"
        ></textarea>
        <p v-if="unblockModal.reason.length > 0 && unblockModal.reason.length < 5" class="text-xs text-red-500 mt-1">
          Minimo 5 caratteri.
        </p>
      </template>
      <template #footer>
        <button
          class="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800 transition"
          @click="closeUnblockModal"
        >
          Annulla
        </button>
        <button
          class="px-4 py-2 text-sm rounded-lg bg-green-600 hover:bg-green-700 text-white transition disabled:opacity-50"
          :disabled="unblockModal.reason.length < 5 || unblockModal.loading"
          @click="confirmUnblock"
        >
          {{ unblockModal.loading ? 'In corso...' : 'Conferma – Sblocca' }}
        </button>
      </template>
    </flow-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      filters: {
        q: '',
        profile_type: '',
      },
      searchTimer: null,
      sortByUnpaid: false,
      unblockModal: {
        open: false,
        user: null,
        reason: '',
        loading: false,
      },
    };
  },
  computed: {
    ...mapGetters(['antifrode_blockedUsers', 'antifrode_loading']),
    blockedUsers() {
      const list = [...(this.antifrode_blockedUsers || [])];
      if (this.sortByUnpaid) list.sort((a, b) => (b.unpaid_eur || 0) - (a.unpaid_eur || 0));
      return list;
    },
    loading() {
      return this.antifrode_loading;
    },
  },
  mounted() {
    this.refresh();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    refresh() {
      this.$store.dispatch('fetchAntifrodeBlockedUsers', {
        q: this.filters.q,
        profile_type: this.filters.profile_type,
        limit: 100,
      });
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.refresh(), 400);
    },
    openUnblockModal(user) {
      this.unblockModal = { open: true, user, reason: '', loading: false };
    },
    closeUnblockModal() {
      this.unblockModal = { open: false, user: null, reason: '', loading: false };
    },
    async confirmUnblock() {
      this.unblockModal.loading = true;
      try {
        await this.$store.dispatch('antifrode_unblockUser', {
          uid: this.unblockModal.user.uid,
          reason: this.unblockModal.reason,
        });
        this.closeUnblockModal();
        this.refresh();
      } catch (e) {
        console.error('Sblocco fallito', e);
        this.unblockModal.loading = false;
      }
    },
    // ── helpers UI ────────────────────────────────────────────────
    profileEmoji(type) {
      return { FOREIGN: '🌍', PRIVATE: '🧍', BUSINESS: '🏢' }[type] || '❓';
    },
    profileBadge(type) {
      return {
        FOREIGN: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        PRIVATE: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        BUSINESS: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      }[type] || 'bg-gray-100 text-gray-500';
    },
    formatEur(val) {
      if (!val) return '€0';
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }).format(val);
    },
    translateReason(reason) {
      if (!reason) return reason;
      // Pattern: auto-blacklist: shared card {brand} *{last4} (fp {fp}) used by {n} users, {m} unpaid recharges
      const m = reason.match(
        /^auto-blacklist:\s*shared card\s+(.+?)\s+\*(\w+)\s+\(fp\s+(\S+)\)\s+used by\s+(\d+)\s+users?,\s*(\d+)\s+unpaid recharges?/i,
      );
      if (m) {
        const [, brand, last4, fp, nUsers, nRicariche] = m;
        return `Blocco automatico: carta condivisa ${brand} *${last4} (fp ${fp}) usata da ${nUsers} utent${nUsers === '1' ? 'e' : 'i'}, ${nRicariche} ricaric${nRicariche === '1' ? 'a' : 'he'} non pagar${nRicariche === '1' ? 'a' : 'e'}`;
      }
      return reason;
    },
  },
};
</script>

<style></style>
